import { useRef, useState } from "react";
import {
  motion,
  useAnimationFrame,
  useMotionValue,
  useScroll,
  useVelocity,
  useReducedMotion,
} from "framer-motion";
import { MARQUEE_ITEMS } from "@/data/site";

const Item = ({ item }) => {
  if (item.type === "dot") {
    return (
      <span
        className="mx-8 inline-block h-6 w-6 shrink-0 rounded-full animate-dot-pulse md:h-8 md:w-8"
        style={{ backgroundColor: item.color }}
        aria-hidden
      />
    );
  }
  return (
    <span
      className="mx-8 shrink-0 font-heading text-6xl font-black uppercase tracking-tight md:text-8xl"
      style={
        item.filled
          ? { color: "#0A0A0A" }
          : { color: "transparent", WebkitTextStroke: "2px #0A0A0A" }
      }
    >
      {item.value}
    </span>
  );
};

export const Marquee = () => {
  const reduce = useReducedMotion();
  const x = useMotionValue(0);
  const trackRef = useRef(null);
  const halfRef = useRef(0);
  const [paused, setPaused] = useState(false);
  const drag = useRef({ active: false, startX: 0, startVal: 0 });

  const { scrollY } = useScroll();
  const scrollVel = useVelocity(scrollY);

  const items = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS];

  useAnimationFrame((_, delta) => {
    if (reduce) return;
    if (!halfRef.current && trackRef.current) {
      halfRef.current = trackRef.current.scrollWidth / 3;
    }
    const half = halfRef.current || 1;
    if (!paused && !drag.current.active) {
      const boost = Math.min(Math.abs(scrollVel.get()) / 60, 120);
      const speed = 60 + boost; // px/s
      let next = x.get() - (speed * delta) / 1000;
      if (next <= -half) next += half;
      x.set(next);
    }
  });

  const onPointerDown = (e) => {
    drag.current = { active: true, startX: e.clientX, startVal: x.get() };
    setPaused(true);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const half = halfRef.current || 1;
    let next = drag.current.startVal + (e.clientX - drag.current.startX);
    next = ((next % half) - half) % half;
    x.set(next);
  };
  const onPointerUp = () => {
    drag.current.active = false;
    setPaused(false);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
  };

  return (
    <section
      id="collection"
      data-testid="section-collection"
      className="overflow-hidden border-t border-hair py-12 md:py-16"
    >
      <div className="mx-auto mb-12 max-w-[1440px] px-5 md:px-10">
        <span className="eyebrow">Kolekcja nieskończonych możliwości</span>
      </div>
      <div
        className="cursor-grab overflow-hidden active:cursor-grabbing"
        data-testid="marquee"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => !drag.current.active && setPaused(false)}
        onPointerDown={onPointerDown}
      >
        <motion.div ref={trackRef} className="flex w-max items-center" style={{ x }}>
          {items.map((item, i) => (
            <Item key={i} item={item} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
